import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Command, CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { toast } from "sonner";
import {
  User, Wrench, HardHat, LayoutDashboard, ArrowRight, UserCheck,
  Zap, Megaphone, ScrollText, Download, Plus, Send,
} from "lucide-react";

/**
 * Global ⌘K command palette. Combines a static list of admin routes with
 * live server search against residents / tickets / vendors, plus a top
 * "Quick actions" group for common flows (broadcast announcement, jump to
 * legal-pack sender, jump to ticket queue, jump to finance export).
 */
export default function CommandPalette({ open, onOpenChange, routes = [] }) {
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState({ residents: [], tickets: [], vendors: [] });
  const [loading, setLoading] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Debounced server search — 220ms feels instant without spamming the API.
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setRemote({ residents: [], tickets: [], vendors: [] }); return; }
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/search", { params: { q: q.trim(), limit: 8 } });
        setRemote({
          residents: data.residents || [],
          tickets: data.tickets || [],
          vendors: data.vendors || [],
        });
      } catch (e) {
        setRemote({ residents: [], tickets: [], vendors: [] });
      }
      setLoading(false);
    }, 220);
    return () => searchRef.current && clearTimeout(searchRef.current);
  }, [q, open]);

  useEffect(() => {
    if (!open) { setQ(""); setRemote({ residents: [], tickets: [], vendors: [] }); }
  }, [open]);

  const filteredRoutes = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return routes.slice(0, 12);
    return routes.filter((r) => r.label.toLowerCase().includes(query)).slice(0, 15);
  }, [q, routes]);

  const go = (path) => { onOpenChange(false); navigate(path); };
  const runAction = (action) => {
    if (action.type === "nav") { go(action.target); return; }
    if (action.type === "broadcast") {
      onOpenChange(false);
      // Small tick so the palette can unmount cleanly before the dialog paints
      setTimeout(() => setBroadcastOpen(true), 60);
    }
  };

  // Quick actions — every one lists a keyword blob so the palette's fuzzy
  // matcher surfaces them even when the user types the action's synonym.
  const QUICK_ACTIONS = useMemo(() => [
    {
      id: "create-ticket", label: "Create maintenance ticket",
      keywords: "new ticket maintenance work order create",
      hint: "Jump to the maintenance queue",
      icon: Wrench, type: "nav", target: "/admin/maintenance?compose=1",
    },
    {
      id: "send-announcement", label: "Send announcement",
      keywords: "broadcast notify email push message all residents",
      hint: "Compose here — no page switch",
      icon: Megaphone, type: "broadcast",
    },
    {
      id: "send-legal-pack", label: "Send legal pack to counsel",
      keywords: "docusign counsel legal terms privacy dpa envelope sign",
      hint: "Open Settings and auto-launch the envelope",
      icon: ScrollText, type: "nav", target: "/admin/settings?legal_pack=1",
    },
    {
      id: "export-ledger", label: "Export ledger",
      keywords: "download csv finance ledger balance aging export",
      hint: "Finance → Ledger CSV",
      icon: Download, type: "nav", target: "/admin/finance?export=1",
    },
  ], []);

  const filteredActions = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return QUICK_ACTIONS;
    return QUICK_ACTIONS.filter((a) =>
      a.label.toLowerCase().includes(query) || a.keywords.includes(query),
    );
  }, [q, QUICK_ACTIONS]);

  const impersonate = async (rid, name) => {
    onOpenChange(false);
    try {
      const { data: r } = await api.post(`/admin/impersonate/${rid}`);
      toast.success(`Now viewing as ${r.target.name || name}`);
      const dest = r.target.role === "vendor" ? "/vendor/tickets" : "/resident/dashboard";
      window.location.href = dest;
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <>
      <CommandDialog open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">
          Run quick actions, search admin pages, residents, tickets, and vendors.
        </DialogPrimitive.Description>
        <CommandInput
          placeholder="Try a command… (announcement, ticket, resident, page)"
          value={q}
          onValueChange={setQ}
          data-testid="cmdk-input"
        />
        <CommandList data-testid="cmdk-list">
          <CommandEmpty>
            {loading ? "Searching…" : q.trim().length < 2
              ? "Type at least 2 characters to search people, tickets, or vendors."
              : "No matches. Try a name, email, unit, or ticket title."}
          </CommandEmpty>

          {/* --- Quick actions ------------------------------------- */}
          {filteredActions.length > 0 && (
            <CommandGroup heading="Quick actions">
              {filteredActions.map((a) => {
                const Icon = a.icon;
                return (
                  <CommandItem
                    key={a.id}
                    value={`action ${a.label} ${a.keywords}`}
                    onSelect={() => runAction(a)}
                    data-testid={`cmdk-action-${a.id}`}
                    className="cursor-pointer"
                  >
                    <Icon className="text-orange-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{a.label}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{a.hint}</div>
                    </div>
                    <Zap className="h-3 w-3 text-orange-400/60 ml-auto" />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {/* --- Pages ------------------------------------------------ */}
          {filteredRoutes.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={q ? "Pages" : "Jump to page"}>
                {filteredRoutes.map((r) => {
                  const Icon = r.icon || LayoutDashboard;
                  return (
                    <CommandItem
                      key={r.to}
                      value={`page ${r.label} ${r.group || ""}`}
                      onSelect={() => go(r.to)}
                      data-testid={`cmdk-page-${r.to}`}
                      className="cursor-pointer"
                    >
                      <Icon />
                      <span>{r.label}</span>
                      {r.group && (
                        <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">
                          {r.group}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}

          {/* --- Residents ------------------------------------------- */}
          {remote.residents.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={`Residents (${remote.residents.length})`}>
                {remote.residents.map((r) => (
                  <CommandItem
                    key={r.id}
                    value={`resident ${r.name} ${r.email} ${r.unit || ""}`}
                    onSelect={() => go(`/admin/residents/${r.id}`)}
                    data-testid={`cmdk-resident-${r.id}`}
                    className="cursor-pointer flex items-center gap-2"
                  >
                    <User />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{r.name || r.email}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {r.email}{r.unit ? ` · Unit ${r.unit}` : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); impersonate(r.id, r.name); }}
                      className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] uppercase tracking-widest border border-orange-500/40 text-orange-300 bg-orange-500/10 hover:bg-orange-500/20"
                      data-testid={`cmdk-impersonate-${r.id}`}
                      aria-label={`Impersonate ${r.name || r.email}`}
                    >
                      <UserCheck className="h-3 w-3" /> Impersonate
                    </button>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* --- Tickets --------------------------------------------- */}
          {remote.tickets.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={`Tickets (${remote.tickets.length})`}>
                {remote.tickets.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={`ticket ${t.title} ${t.id}`}
                    onSelect={() => go(`/admin/maintenance?ticket=${t.id}`)}
                    data-testid={`cmdk-ticket-${t.id}`}
                    className="cursor-pointer"
                  >
                    <Wrench />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{t.title || `Ticket ${t.id}`}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {t.id?.slice(0, 8)}… · {t.status || "open"}
                        {t.priority ? ` · ${t.priority}` : ""}
                      </div>
                    </div>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* --- Vendors --------------------------------------------- */}
          {remote.vendors.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={`Vendors (${remote.vendors.length})`}>
                {remote.vendors.map((v) => (
                  <CommandItem
                    key={v.id}
                    value={`vendor ${v.name} ${v.company || ""} ${v.email}`}
                    onSelect={() => go(`/admin/vendors?highlight=${v.id}`)}
                    data-testid={`cmdk-vendor-${v.id}`}
                    className="cursor-pointer"
                  >
                    <HardHat />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{v.name || v.company}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {v.email}{v.company ? ` · ${v.company}` : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); impersonate(v.id, v.name); }}
                      className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] uppercase tracking-widest border border-emerald-500/40 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20"
                      data-testid={`cmdk-impersonate-${v.id}`}
                      aria-label={`Impersonate ${v.name || v.email}`}
                    >
                      <UserCheck className="h-3 w-3" /> View as
                    </button>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          <CommandSeparator />
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-3">
            <span>⌘K to toggle</span>
            <span>↑↓ to navigate</span>
            <span>↵ to open</span>
            <CommandShortcut className="ml-auto">Esc to close</CommandShortcut>
          </div>
        </CommandList>
      </CommandDialog>

      <BroadcastAnnouncementDialog open={broadcastOpen} onOpenChange={setBroadcastOpen} />
    </>
  );
}

/**
 * Inline broadcast dialog — palette action "Send announcement" opens this
 * without leaving the current page. POSTs to /api/notifications/broadcast.
 */
function BroadcastAnnouncementDialog({ open, onOpenChange }) {
  const { properties, currentProperty } = useProperty();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("residents"); // residents | all | property
  const [propertyId, setPropertyId] = useState(currentProperty?.id || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(""); setBody("");
      setAudience("residents");
      setPropertyId(currentProperty?.id || "");
    }
  }, [open, currentProperty]);

  const send = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Add a title and a body");
    const payload = { title: title.trim(), body: body.trim(), type: "broadcast" };
    if (audience === "residents") payload.role = "resident";
    if (audience === "property") {
      if (!propertyId) return toast.error("Pick a property");
      payload.property_id = propertyId;
      payload.role = "resident";
    }
    if (audience === "all") {
      // Broadcast endpoint requires user_ids OR role OR property_id — send to
      // every resident across every property.
      payload.role = "resident";
    }
    setSending(true);
    try {
      const { data } = await api.post("/notifications/broadcast", payload);
      toast.success(`Announcement sent to ${data.recipients} resident${data.recipients === 1 ? "" : "s"}`);
      onOpenChange(false);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="broadcast-dialog">
        <DialogHeader>
          <DialogTitle><Megaphone className="h-4 w-4 inline mr-1 text-orange-400" /> Send announcement</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Fans out in-app notification, push, and (where opted in) email — no page switch needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input
              data-testid="broadcast-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Pool closed for maintenance"
              maxLength={100}
            />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              data-testid="broadcast-body"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Water heater work on Tuesday from 9am to noon. We'll send a follow-up when it's back online."
              maxLength={500}
            />
          </div>
          <div>
            <Label>Audience</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="broadcast-audience" className="bg-white/[0.02] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="residents">Every resident (portfolio-wide)</SelectItem>
                <SelectItem value="property">Residents of one property</SelectItem>
                <SelectItem value="all">All residents (portfolio-wide, alias)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {audience === "property" && (
            <div>
              <Label>Property</Label>
              <Select value={propertyId} onValueChange={setPropertyId}>
                <SelectTrigger data-testid="broadcast-property" className="bg-white/[0.02] border-white/10">
                  <SelectValue placeholder="Choose a community" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            data-testid="broadcast-send"
            onClick={send}
            disabled={sending}
            className="w-full bg-orange-500 hover:bg-orange-600"
          >
            <Send className="h-4 w-4 mr-1" /> {sending ? "Sending…" : "Send announcement"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
